import { useState, useRef, useEffect } from "react";
import { Overlay, Popover } from "react-bootstrap";
import Calendar from "react-calendar";
import { formatDateISOStringToMMDDYYYY } from "utils";
import { scheduleState, cartState } from "atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import "react-calendar/dist/Calendar.css";
import { CartModel } from "atoms/models";
import { appointmentsState } from "atoms";
import { SCHEDULE_TYPE_NON_SECURITY, SCHEDULE_TYPE_SECURITY } from "utils/const";

const CalendarButton: React.FC<{ appointments: any; label: string; scheduleType: string }> = ({ appointments, label, scheduleType }) => {
  const allAppointments = useRecoilValue(appointmentsState);
  const [showAppointmentPopover, setShowAppointmentPopover] = useState(false);
  const [overlayTarget, setOverlayTarget] = useState(null);
  const ref = useRef(null);
  const [date, setDate] = useState<Date | null>(new Date());
  const [appointment, setAppointment] = useState<any>([]);
  const [schedule, setSchedule] = useRecoilState(scheduleState);
  const cart = useRecoilValue(cartState);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !(ref.current as any).contains(event.target)) {
        // Clicked outside the popover, close it
        setShowAppointmentPopover(false);
      }
    };

    // Add event listener to handle clicks outside the popover
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      // Clean up event listener on component unmount
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Click handler for button
  const handleClick = (event: any) => {
    setShowAppointmentPopover(!showAppointmentPopover); // Toggle popover visibility
    setOverlayTarget(event.target); // Set target element for popover
  };

  // Find appointment by date
  function findAppointmentByDate(date: string) {
    return appointments.find((appointment: any) => appointment.appointment.workDate === date);
  }

  // Change handler for calendar
  const handleChange = (value: any) => {
    if (value instanceof Date) {
      const formattedDate = formatDateISOStringToMMDDYYYY(value); // Format date
      const appointmentObject = findAppointmentByDate(formattedDate); // Find appointment for selected date
      setAppointment(appointmentObject.appointment); // Set selected appointment
      setDate(value); // Set selected date
      if (scheduleType === SCHEDULE_TYPE_NON_SECURITY) {
        setSchedule({
          ...schedule,
          nonSecuritySchedule: {
            ...schedule.nonSecuritySchedule,
            workDate: formattedDate,
            workTime: null,
            workTimeFriendly: null,
          },
        });
      } else {
        setSchedule({
          ...schedule,
          securitySchedule: {
            ...schedule.securitySchedule,
            workDate: formattedDate,
            workTime: null,
            workTimeFriendly: null,
          },
        });
      }
    }
  };

  const isDateAvailable = (date: Date) => {
    // Convert the provided date to a string in the format MM/DD/YYYY.
    const dateString = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    // Check if the converted date string matches any of the available appointment dates.
    const available = appointments.some((item: any) => item.appointment.workDate === dateString);

    // Create a new Date object for a specific non-security product schedule date.
    const selectedNonSecurityProductScheduleDate = new Date(schedule.nonSecuritySchedule.workDate);

    // Check if the schedule type is "security" and a non-security schedule date is set.
    if (scheduleType === SCHEDULE_TYPE_SECURITY && selectedNonSecurityProductScheduleDate) {
      // Create a new date object representing the next day after the non-security product schedule date.
      const nextDay: any = new Date(selectedNonSecurityProductScheduleDate);
      nextDay.setDate(selectedNonSecurityProductScheduleDate.getDate());

      // If the provided date is before or on the non-security schedule date, it's not available.
      if (date < nextDay) {
        return false; // Date is not available for scheduling.
      }
    }

    // Return true if the date is available, otherwise false.
    return available;
  };

  // Disable date tiles on calendar for unavailable dates
  const tileDisabled = ({ date }: { date: Date }) => !isDateAvailable(date);

  // Format weekday abbreviations
  const formatShortWeekday = (locale: any, date: Date) => {
    const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    return weekdays[date.getDay()];
  };

  // Set appointment time based on selection
  const handleSettingAppointmentTime = (time: any) => {
    if (scheduleType === SCHEDULE_TYPE_NON_SECURITY) {
      setSchedule({
        ...schedule,
        nonSecuritySchedule: {
          ...schedule.nonSecuritySchedule,
          workDate: appointment.workDate,
          workTime: time.appointment.time,
          workTimeFriendly: time.appointment.friendlyTime,
        },
      });
    } else {
      setSchedule({
        ...schedule,
        securitySchedule: {
          ...schedule.securitySchedule,
          workDate: appointment.workDate,
          workTime: time.appointment.time,
          workTimeFriendly: time.appointment.friendlyTime,
        },
      });
    }
  };

  // Set schedule flexibility by type
  const setScheduleByType = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (scheduleType === SCHEDULE_TYPE_NON_SECURITY) {
      setSchedule({
        ...schedule,
        nonSecuritySchedule: {
          ...schedule.nonSecuritySchedule,
          asap: e.target.checked,
        },
      });
    } else {
      setSchedule({
        ...schedule,
        securitySchedule: {
          ...schedule.securitySchedule,
          asap: e.target.checked,
        },
      });
    }
  };

  const setScrollBehaviorOnDateAndTimeButton: any = (cart: CartModel, label: string) => {
    if (window.innerWidth < 577) {
      if (cart.schedule.availableInstallTypes.length > 1) {
        return { top: 680, behavior: "smooth" };
      } else {
        if (!cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation) {
          return { top: 280, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && !cart.schedule.needsNonSecurityInstallation) {
          return { top: 280, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation && label.includes("Security")) {
          return { top: 470, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation && !label.includes("Security")) {
          return { top: 280, behavior: "smooth" };
        } else {
          return { top: 280, behavior: "smooth" };
        }
      }
    } else if (window.innerWidth > 577) {
      if (cart.schedule.availableInstallTypes.length > 1) {
        return { top: 580, behavior: "smooth" };
      } else {
        if (!cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation) {
          return { top: 230, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && !cart.schedule.needsNonSecurityInstallation) {
          return { top: 230, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation && label.includes("Security")) {
          return { top: 360, behavior: "smooth" };
        } else if (cart.schedule.needsSecurityInstallation && cart.schedule.needsNonSecurityInstallation && !label.includes("Security")) {
          return { top: 230, behavior: "smooth" };
        } else {
          return { top: 230, behavior: "smooth" };
        }
      }
    }
  };

  return (
    <div ref={ref} className="calendar-button-container">
      <div className="calendar-button-and-display">
        {/* Button to toggle popover */}
        <button
          className="btn btn-primary mt-5 btn-date-time-select"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            handleClick(e); // Toggle popover visibility
            window.scrollTo(setScrollBehaviorOnDateAndTimeButton(cart, label));
          }}
          disabled={
            scheduleType === SCHEDULE_TYPE_SECURITY &&
            allAppointments?.schedules?.nonSecuritySchedule?.availableAppointments.length > 0 &&
            !schedule.nonSecuritySchedule.workDate
          }
        >
          <i className="fa-solid fa-calendar-days me-1"></i>
          {label}
        </button>
        {/* Display selected appointment */}
        {scheduleType === SCHEDULE_TYPE_NON_SECURITY ? (
          <div className="appointment-display ms-3 mt-3">
            <strong>
              {schedule?.nonSecuritySchedule?.workDate && schedule?.nonSecuritySchedule?.workTimeFriendly && (
                <>
                  {schedule?.nonSecuritySchedule?.workDate} - {schedule?.nonSecuritySchedule?.workTimeFriendly}
                </>
              )}
            </strong>
          </div>
        ) : (
          <div className="appointment-display ms-3 mt-3">
            <strong>
              {schedule?.securitySchedule?.workDate && schedule?.securitySchedule?.workTimeFriendly && (
                <>
                  {schedule?.securitySchedule?.workDate} - {schedule?.securitySchedule?.workTimeFriendly}
                </>
              )}
            </strong>
          </div>
        )}
      </div>

      {/* Popover for calendar and appointment selection */}
      <Overlay show={showAppointmentPopover} target={overlayTarget} placement="bottom" container={ref} containerPadding={20} rootClose={true}>
        <Popover id="popover-contained">
          <Popover.Header as="h3">{label}</Popover.Header>
          <Popover.Body>
            {/* Calendar component */}
            <Calendar
              onChange={handleChange}
              value={date}
              view="month"
              className="schedule-popup-calendar"
              tileDisabled={tileDisabled}
              calendarType="hebrew"
              formatShortWeekday={formatShortWeekday}
            />
            {/* handleMonthChange */}
            <div className="text-center mt-2 mb-3">Preferred Time of Day</div>
            <div className="friendly-time-container">
              <div>
                {/* List of available appointment times */}
                {appointment?.workTime?.length > 0
                  ? appointment.workTime.map((time: any, index: number) => {
                      if (scheduleType === "nonSecurity") {
                        return (
                          <div key={index} className="form-check" onClick={() => setShowAppointmentPopover(false)}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="frindlyTime"
                              id={`frindlyTime-${index}`}
                              checked={time.appointment.friendlyTime === schedule.nonSecuritySchedule.workTimeFriendly}
                              onChange={() => {
                                handleSettingAppointmentTime(time);
                              }}
                            />
                            <label className="form-check-label" htmlFor={`frindlyTime-${index}`}>
                              {time.appointment.friendlyTime}
                            </label>
                          </div>
                        );
                      } else if (scheduleType === "security") {
                        return (
                          <div key={index} className="form-check" onClick={() => setShowAppointmentPopover(false)}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name="frindlyTime"
                              id={`frindlyTime${index}`}
                              checked={time.appointment.friendlyTime === schedule.securitySchedule.workTimeFriendly}
                              onChange={() => {
                                handleSettingAppointmentTime(time);
                              }}
                            />
                            <label className="form-check-label" htmlFor={`frindlyTime${index}`}>
                              {time.appointment.friendlyTime}
                            </label>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })
                  : null}
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
      {/* Checkbox for schedule flexibility */}
      <div className="form-check mt-2 ps-0">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`flexSwitchCheckDefault-${scheduleType}`}
            checked={scheduleType === SCHEDULE_TYPE_NON_SECURITY ? schedule.nonSecuritySchedule.asap : schedule.securitySchedule.asap}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScheduleByType(e)}
            disabled={scheduleType === SCHEDULE_TYPE_NON_SECURITY ? !schedule?.nonSecuritySchedule?.workDate : !schedule?.securitySchedule?.workDate}
          />
          <label className="form-check-label" htmlFor={`flexSwitchCheckDefault-${scheduleType}`}>
            I’m flexible. Call me if an earlier appointment becomes available.
          </label>
        </div>
      </div>
    </div>
  );
};

export default CalendarButton;
